import { useEffect, useRef, useState } from 'react';
import { clsx } from '../../utils/cn.js';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

const FROM_OFFSET = {
  up: 'translate-y-8',
  down: '-translate-y-8',
  left: '-translate-x-10',
  right: 'translate-x-10',
  none: '',
};

/**
 * Aparición suave al entrar en viewport. Respeta prefers-reduced-motion.
 * @param {'up' | 'down' | 'left' | 'right' | 'none'} [from]
 * @param {boolean} [scale] — ligero zoom al entrar
 */
export default function ScrollReveal({
  children,
  className,
  delayMs = 0,
  from = 'up',
  scale = false,
}) {
  const ref = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  const [visible, setVisible] = useState(reducedMotion);
  const offset = FROM_OFFSET[from] ?? FROM_OFFSET.up;

  useEffect(() => {
    if (reducedMotion) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reducedMotion]);

  return (
    <div
      ref={ref}
      className={clsx(
        reducedMotion && 'translate-x-0 translate-y-0 scale-100 opacity-100',
        !reducedMotion &&
          visible &&
          'translate-x-0 translate-y-0 scale-100 opacity-100',
        !reducedMotion &&
          !visible &&
          clsx(offset, scale && 'scale-[0.94]', 'opacity-0'),
        !reducedMotion &&
          !visible &&
          'will-change-[opacity,transform]',
        !reducedMotion &&
          'transition-[opacity,transform] duration-[680ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
        className
      )}
      style={
        !reducedMotion
          ? { transitionDelay: visible ? `${delayMs}ms` : '0ms' }
          : undefined
      }
    >
      {children}
    </div>
  );
}
